<template>
    <div>
        <b-modal
            v-model="modalShow"
            size="lg"
            title="ネットワーク接続のテスト"
            @hidden="close"
            @ok="close"
            ok-variant="yokumiru-gray"
            ok-title="閉じる"
            ok-only>
            <div class="modal-body">
                <div class="container">
                    <template v-if="isStatsSuccess">
                        <div class="testResult">
                            <div>
                                <h6 class="success"><b-icon-check-circle-fill/>&nbsp;ネットワーク接続は正常です。</h6>
                                <p class="mb-0">お使いの環境は、ビデオ通話をご利用いただける環境です。</p>
                            </div>
<!--                            <div-->
<!--                                v-if="isDebug"-->
<!--                                class="mt-3">-->
<!--                                {{ stats.uplink }}-->
<!--                                <hr>-->
<!--                                {{ stats.downlink }}-->
<!--                            </div>-->
                        </div>
                    </template>
                    <template v-else-if="isReachedNetworkTimeout && !isStatsSuccess">
                        <div class="testResult">
                            <h6><b-icon-exclamation-triangle-fill/>&nbsp;ネットワーク接続に問題があります。</h6>
                            <p v-if="isStatsAudioSuccess">マイクは正常ですが、カメラの通信に問題があるようです。</p>
                            <p v-else-if="isStatsVideoSuccess">カメラは正常ですが、マイクの通信に問題があるようです。</p>
                            <p v-else>お使いの環境では、ビデオ通話に必要なネットワーク接続が行えないようです。</p>
                            <ol>
                                <li>セキュリティソフトやポート制限により、通信がブロックされている可能性があります。</li>
                                <li>マイクやカメラのテストをまだ行っていない場合、そちらも併せてお試しください。</li>
                                <li>上手くいかない場合、他のインターネット回線や別のデバイスからもテストをお試しください。</li>
                            </ol>
                        </div>
                    </template>
                    <template v-else>
                        <div class="d-flex justify-content-center align-items-center">
                            <div class="text-center">
                                <b-spinner variant="yokumiru-user"/>
                                <div class="text-muted mt-2">ネットワーク接続のテスト中です…</div>
                            </div>
                        </div>
                    </template>
                </div>
            </div>
        </b-modal>
    </div>
</template>

<script>
import AgoraRTC from 'agora-rtc-sdk-ng'
import _ from 'lodash'
import constants from "../../../constants"

export default {
    name: 'VideoTestModal',

    props: {
        userType: {
            type: String,
            default: 'user',
        },
        consultation: {
            type: Object,
            required: true,
        },
        roomKey: {
            type: String,
            required: true,
        },
        apiKey: {
            type: String,
            required: true,
        },
        uplinkToken: {
            type: String,
            required: true,
        },
        downlinkToken: {
            type: String,
            required: true,
        },
        // @see https://docs.agora.io/en/Video/API%20Reference/web_ng/interfaces/clientconfig.html#mode
        transcode: {
            type: String,
            default: 'rtc',
        },
        debug: {
            type: String,
            default: '0',
        },
    },

    data: function () {
        return {
            modalShow: false,
            uplinkClient: null,
            downlinkClient: null,
            localVideo: null,
            localAudio: null,
            timer: {
                clientJoin: null,
                localDevice: null,
                networkTimeout: null,
            },
            videos: [],
            audios: [],
            selectedVideoId: null,
            selectedAudioId: null,
            localVideoState: false,
            localAudioState: false,
            stats: {
                uplink: {
                    quality: null,
                    audio: null,
                    video: null,
                },
                downlink: {
                    quality: null,
                    audio: null,
                    video: null,
                },
            },
            inProgress: {
                switchLocalVideo: false,
                switchLocalAudio: false,
            },
            agoraInitState: {
                errorHandled: false,
            },
            errorType: null,
            isReachedNetworkTimeout: false,
        }
    },

    computed: {
        uidInitialUplink: function () {
            // 会員モード = 6, 医師モード = 8
            return this.userType === 'user' ? '6' : '8'
        },

        uidInitialDownlink: function () {
            // 会員モード = 7, 医師モード = 9
            return this.userType === 'user' ? '7' : '9'
        },

        /**
         * agoraセッションにおけるユニークID
         * 先頭の数字でアップロードのネットワーク接続テストと見なす（他のイベントリスナで検知させない）
         *
         * @return {number}
         */
        uidUplink: function () {
            const id = this.uidInitialUplink + this.consultation.id.toString()
            return Number(id)
        },

        /**
         * agoraセッションにおけるユニークID
         * 先頭の数字でダウンロードのネットワーク接続テストと見なす（他のイベントリスナで検知させない）
         *
         * @return {number}
         */
        uidDownlink: function () {
            const id = this.uidInitialDownlink + this.consultation.id.toString()
            return Number(id)
        },

        /**
         * デバッグモード判定
         *
         * @return {boolean}
         */
        isDebug: function () {
            return this.debug === '1'
        },

        /**
         * CloudWatch Logs 出力時のユーザタイプフォーマット
         *
         * @return {string}
         */
        cwLogsUserType: function () {
            return this.userType === 'user' ? 'user:web (network-test)' : `${this.userType} (network-test)`
        },

        /**
         * ネットワーク接続の正常判定（マイク）
         *
         * @return {boolean}
         */
        isStatsAudioSuccess: function () {
            return this.stats.uplink.quality === 1 &&
                this.stats.uplink.audio &&
                this.stats.uplink.audio.sendPackets > 0 &&
                this.stats.downlink.quality === 1 &&
                this.stats.downlink.audio &&
                this.stats.downlink.audio.receivePackets > 0
        },

        /**
         * ネットワーク接続の正常判定（カメラ）
         *
         * @return {boolean}
         */
        isStatsVideoSuccess: function () {
            return this.stats.uplink.quality === 1 &&
                this.stats.uplink.video &&
                this.stats.uplink.video.sendPackets > 0 &&
                this.stats.downlink.quality === 1 &&
                this.stats.downlink.video &&
                this.stats.downlink.video.receivePackets > 0
        },

        /**
         * ネットワーク接続の正常判定
         *
         * @return {boolean}
         */
        isStatsSuccess: function () {
            return this.isStatsAudioSuccess && this.isStatsVideoSuccess
        },
    },

    watch: {
        selectedVideoId: function () {
            this.switchVideo()
        },

        selectedAudioId: function () {
            this.switchAudio()
        },

        errorType: function (val) {
            if (val !== null) {
                this.clearAllMediaAndLeave()
                this.clearNetworkStats()
            }
        },

        isReachedNetworkTimeout: function (val) {
            if (val === true && !this.isStatsSuccess) {
                this.clearAllMediaAndLeave()
                this.clearNetworkStats()
            }
        },

        isStatsVideoSuccess: function (val) {
            if (val) {
                this.queueCwLogs(`[${this.cwLogsUserType}] video network test succeeded.`)
            }
        },

        isStatsAudioSuccess: function (val) {
            if (val) {
                this.queueCwLogs(`[${this.cwLogsUserType}] audio network test succeeded.`)
            }
        },
    },

    created: function () {
        bus.$on('open-modal-network-test', this.open)
    },

    beforeDestroy: function () {
        bus.$off('open-modal-network-test')
    },

    methods: {
        /**
         * モーダル表示
         */
        open: function () {
            this.modalShow = true
            this.initAgora()
        },

        /**
         * モーダル非表示
         */
        close: function () {
            this.clearAllMediaAndLeave()
            this.clearTestState()
            this.clearNetworkStats()
            this.modalShow = false
        },

        /**
         * 閉じるボタン押下時
         */
        handleOk: function (e) {
            e.preventDefault()
            this.close()
        },

        /**
         * CloudWatch Logs キュー追加
         *
         * @param {string} message
         */
        queueCwLogs: function (message) {
            bus.$emit('queue-cloud-watch-logs-user', message)
        },

        /**
         * 初期化処理
         */
        initAgora: async function () {
            if (this.uplinkClient !== null || this.downlinkClient !== null) {
                return
            }

            if (!AgoraRTC.checkSystemRequirements()) {
                this.queueCwLogs(`[${this.cwLogsUserType}] AgoraRTC.checkSystemRequirements(): System requirements not met.`)
            }

            AgoraRTC.setLogLevel(4)

            /**
             * @type {IAgoraRTCClient}
             * @see https://docs.agora.io/en/Video/API%20Reference/web_ng/interfaces/iagorartc.html#createclient
             * @see https://docs.agora.io/en/Video/API%20Reference/web_ng/interfaces/clientconfig.html
             */
            this.uplinkClient = AgoraRTC.createClient({ mode: this.transcode, codec: 'vp8' })
            this.downlinkClient = AgoraRTC.createClient({ mode: this.transcode, codec: 'vp8' })

            // イベントリスナ追加
            this.subscribeStreamEvents()

            // client.joinのタイムアウト用
            this.timer.clientJoin = setTimeout(() => this.errorType = 'client.join', 10000)

            // ネットワーク接続テストのタイムアウト用
            this.timer.networkTimeout = setTimeout(() => this.isReachedNetworkTimeout = true, 12000)

            /**
             * @see https://docs.agora.io/en/Video/API%20Reference/web_ng/interfaces/iagorartcclient.html#join
             */
            await this.uplinkClient.join(this.apiKey, this.roomKey, this.uplinkToken, this.uidUplink)
                .then((uid) => {
                    clearTimeout(this.timer.clientJoin)
                    this.queueCwLogs(`[${this.cwLogsUserType}] client join success (uplink client). uid:${uid}`)
                    this.timer.localDevice = setInterval(() => this.updateLocalDevices(), 2000)
                })
                .catch((e) => {
                    clearTimeout(this.timer.clientJoin)
                    this.agoraInitState.errorHandled = true
                    this.queueCwLogs(`[${this.cwLogsUserType}] client join failed (uplink client). ${e}`)
                })

            // client.join 後すぐに取得だと取れないケースがあるためディレイをかける
            setTimeout(() => this.logLocalTracks(), 5000)
            if (this.uplinkClient) {
                this.queueCwLogs(`[${this.cwLogsUserType}] agora channelName:${this.uplinkClient.channelName} uid:${this.uplinkClient.uid} connectionState:${this.uplinkClient.connectionState}`)
            }

            await this.downlinkClient.join(this.apiKey, this.roomKey, this.downlinkToken, this.uidDownlink)
                .then((uid) => {
                    this.queueCwLogs(`[${this.cwLogsUserType}] client join success (downlink client). uid:${uid}`)
                })
                .catch((e) => {
                    this.agoraInitState.errorHandled = true
                    this.queueCwLogs(`[${this.cwLogsUserType}] client join failed (downlink client). ${e}`)
                })

            if (this.downlinkClient) {
                this.queueCwLogs(`[${this.cwLogsUserType}] agora channelName:${this.downlinkClient.channelName} uid:${this.downlinkClient.uid} connectionState:${this.downlinkClient.connectionState}`)
            }
        },

        /**
         * uid頭文字を取得
         *
         * @param {number} uid
         * @return {string}
         */
        initialStrNum: function (uid) {
            return (uid + '').slice(0, 1)
        },

        /**
         * Agoraのイベントリスナ追加
         */
        subscribeStreamEvents: function () {
            this.downlinkClient.on('user-published', async (user, mediaType) => {
                // uplinkではない場合
                if (this.initialStrNum(user.uid) !== this.uidInitialUplink) {
                    return
                }

                await this.downlinkClient.subscribe(user, mediaType)

                if (mediaType === 'video') {
                    this.queueCwLogs(`[${this.cwLogsUserType}] subscribe video success.`)
                    // ネットワーク接続テストでは再生不要なのでコメントアウト
                    // this.remoteVideo = user.videoTrack
                    // this.remoteVideo.play('remote-video')
                }
                if (mediaType === 'audio') {
                    this.queueCwLogs(`[${this.cwLogsUserType}] subscribe audio success.`)
                    // ネットワーク接続テストでは再生不要なのでコメントアウト
                    // this.remoteAudio = user.audioTrack
                    // this.remoteAudio.play()
                }
            })

            this.uplinkClient.on('network-quality', (quality) => {
                if (this.isStatsSuccess) {
                    return
                }

                /**
                 * 0: The quality is unknown.
                 * 1: The quality is excellent.
                 * 2: The quality is good, but the bitrate is less than optimal.
                 * 3: Users experience slightly impaired communication.
                 * 4: Users can communicate with each other, but not very smoothly.
                 * 5: The quality is so poor that users can barely communicate.
                 * 6: The network is disconnected and users cannot communicate.
                 */
                this.stats.uplink.quality = quality.uplinkNetworkQuality
                this.stats.uplink.audio = this.uplinkClient.getLocalAudioStats()
                this.stats.uplink.video = this.uplinkClient.getLocalVideoStats()

                this.queueCwLogs(`[${this.cwLogsUserType}] uplink network quality ${quality.uplinkNetworkQuality}`)

                if (this.isDebug) {
                    console.log('uplink network quality', quality.uplinkNetworkQuality)
                    console.log('uplink audio stats', this.uplinkClient.getLocalAudioStats())
                    console.log("uplink video stats", this.uplinkClient.getLocalVideoStats())
                }
            })

            this.downlinkClient.on('network-quality', (quality) => {
                if (this.isStatsSuccess) {
                    return
                }

                /**
                 * 0: The quality is unknown.
                 * 1: The quality is excellent.
                 * 2: The quality is good, but the bitrate is less than optimal.
                 * 3: Users experience slightly impaired communication.
                 * 4: Users can communicate with each other, but not very smoothly.
                 * 5: The quality is so poor that users can barely communicate.
                 * 6: The network is disconnected and users cannot communicate.
                 */
                this.stats.downlink.quality = quality.downlinkNetworkQuality
                this.stats.downlink.audio = this.downlinkClient.getRemoteAudioStats()[this.uidUplink]
                this.stats.downlink.video = this.downlinkClient.getRemoteVideoStats()[this.uidUplink]

                this.queueCwLogs(`[${this.cwLogsUserType}] downlink network quality ${quality.downlinkNetworkQuality}`)

                if (this.isDebug) {
                    console.log('downlink network quality', quality.downlinkNetworkQuality)
                    console.log("downlink audio stats", this.downlinkClient.getRemoteAudioStats()[this.uidUplink])
                    console.log("downlink video stats", this.downlinkClient.getRemoteVideoStats()[this.uidUplink])
                }
            })
        },

        /**
         * ビデオの切り替え
         * @see https://docs.agora.io/en/Video/test_switch_device_web_ng?platform=Web
         */
        switchVideo: async function () {
            if (this.selectedVideoId === null || this.inProgress.switchLocalVideo) return
            this.inProgress.switchLocalVideo = true

            if (this.localVideo === null) {
                await this.initLocalVideo()
            }
            else {
                const currentLabel = this.localVideo.getTrackLabel()
                const selectedVideo = this.videos.find((video) => {
                    return video.deviceId === this.selectedVideoId
                })

                // 現在のビデオデバイスと異なるものが選択された場合のみ切り替え
                if (selectedVideo && selectedVideo.label !== currentLabel) {
                    this.localVideo.setDevice(this.selectedVideoId)
                        .then(() => {
                            this.queueCwLogs(`[${this.cwLogsUserType}] switch local video success. label:${selectedVideo.label} deviceId:${selectedVideo.deviceId} groupId:${selectedVideo.groupId}`)
                        })
                        .catch((e) => {
                            this.errorType = 'unknown'
                            this.queueCwLogs(`[${this.cwLogsUserType}] switch local video failed. ${e}`)
                        })
                }
            }

            this.inProgress.switchLocalVideo = false
        },

        /**
         * オーディオの切り替え
         * @see https://docs.agora.io/en/Video/test_switch_device_web_ng?platform=Web
         */
        switchAudio: async function () {
            if (this.selectedAudioId === null || this.inProgress.switchLocalAudio) return
            this.inProgress.switchLocalAudio = true

            if (this.localAudio === null) {
                await this.initLocalAudio()
            }
            else {
                const currentLabel = this.localAudio.getTrackLabel()
                const selectedAudio = this.audios.find((audio) => {
                    return audio.deviceId === this.selectedAudioId
                })

                // 現在のオーディオデバイスと異なるものが選択された場合のみ切り替え
                if (selectedAudio && selectedAudio.label !== currentLabel) {
                    this.localAudio.setDevice(this.selectedAudioId)
                        .then(() => {
                            this.queueCwLogs(`[${this.cwLogsUserType}] switch local audio success. label:${selectedAudio.label} deviceId:${selectedAudio.deviceId} groupId:${selectedAudio.groupId}`)
                        })
                        .catch((e) => {
                            this.errorType = 'unknown'
                            this.queueCwLogs(`[${this.cwLogsUserType}] switch local audio failed. ${e}`)
                        })
                }
            }

            this.inProgress.switchLocalAudio = false
        },

        /**
         * LocalVideoを初期化
         */
        initLocalVideo: async function () {
            if (this.selectedVideoId) {
                try {
                    this.localVideo = await AgoraRTC.createCameraVideoTrack({cameraId: this.selectedVideoId})

                    if (this.isDebug) {
                        // ネットワーク接続テストでは再生不要なのでコメントアウト
                        // local開発時に映像確認したいときは、一時的にコメントを外して DOM に div#local-video-test を追加する
                        // this.localVideo.play('local-video-test')
                    }
                    this.queueCwLogs(`[${this.cwLogsUserType}] create local video success.`)
                }
                catch (e) {
                    this.agoraInitState.errorHandled = true
                    this.errorType = 'permission'
                    this.queueCwLogs(`[${this.cwLogsUserType}] create local video failed. ${e}`)
                    return
                }
            }

            try {
                await this.uplinkClient.publish(this.localVideo)
                this.queueCwLogs(`[${this.cwLogsUserType}] publish local video success.`)
            }
            catch (e) {
                this.agoraInitState.errorHandled = true
                this.errorType = 'unknown'
                this.queueCwLogs(`[${this.cwLogsUserType}] publish local video failed. ${e}`)
            }
        },

        /**
         * LocalAudioを初期化
         */
        initLocalAudio: async function () {
            if (this.selectedAudioId) {
                try {
                    this.localAudio = await AgoraRTC.createMicrophoneAudioTrack({microphoneId: this.selectedAudioId})
                    this.queueCwLogs(`[${this.cwLogsUserType}] create local audio success.`)
                }
                catch (e) {
                    this.agoraInitState.errorHandled = true
                    this.errorType = 'permission'
                    this.queueCwLogs(`[${this.cwLogsUserType}] create local audio failed. ${e}`)
                    return
                }
            }

            try {
                await this.uplinkClient.publish(this.localAudio)
                this.queueCwLogs(`[${this.cwLogsUserType}] publish local audio success.`)
            }
            catch (e) {
                this.agoraInitState.errorHandled = true
                this.errorType = 'unknown'
                this.queueCwLogs(`[${this.cwLogsUserType}] publish local audio failed. ${e}`)
            }
        },

        /**
         * テスト状態のクリア
         */
        clearTestState: function () {
            // client.joinタイムアウトをクリア
            clearTimeout(this.timer.clientJoin)

            // ネットワーク接続タイムアウトをクリア
            clearTimeout(this.timer.networkTimeout)
            this.isReachedNetworkTimeout = false

            // エラー状態を解除
            this.errorType = null
        },

        /**
         * ネットワーク接続ステータスのクリア
         */
        clearNetworkStats: function () {
            this.stats.uplink = {
                quality: null,
                audio: null,
                video: null,
            }
            this.stats.downlink = {
                quality: null,
                audio: null,
                video: null,
            }
        },

        /**
         * すべてのメディアをクリアしてチャンネルから退室
         */
        clearAllMediaAndLeave: function () {
            clearInterval(this.timer.localDevice)
            this.clearLocalMedia()

            // マイクとカメラの許可がされていない場合、clientがnullのままになるケースがあるためif判定
            if (this.uplinkClient) {
                this.uplinkClient.leave()
            }
            if (this.downlinkClient) {
                this.downlinkClient.leave()
            }

            this.videos = []
            this.audios = []
            this.selectedVideoId = null
            this.selectedAudioId = null
            this.localVideoState = false
            this.localAudioState = false

            this.uplinkClient = null
            this.downlinkClient = null
        },

        /**
         * メディアをクリア
         */
        clearLocalMedia: function () {
            if (this.localVideo !== null) {
                this.localVideo.close()
                if (this.uplinkClient) this.uplinkClient.unpublish(this.localVideo)
                this.localVideo = null
            }

            if (this.localAudio !== null) {
                this.localAudio.close()
                if (this.uplinkClient) this.uplinkClient.unpublish(this.localAudio)
                this.localAudio = null
            }
        },

        /**
         * デバイスを監視
         * デバイスの追加・解除が発生した場合は接続状態更新する
         *
         * @see https://developer.mozilla.org/ja/docs/Web/API/MediaDevices/enumerateDevices
         */
        updateLocalDevices: function () {
            AgoraRTC.getCameras().then((devices) => {
                this.updateLocalVideos(devices)
            })
                .catch((e) => {
                    this.handleErrorUpdateLocalDevices(e)
                })

            AgoraRTC.getMicrophones().then((devices) => {
                this.updateLocalAudios(devices)
            })
                .catch((e) => {
                    this.handleErrorUpdateLocalDevices(e)
                })
        },

        handleErrorUpdateLocalDevices: function (e) {
            this.queueCwLogs(`[${this.cwLogsUserType}] update local devices failed. ${e}`)

            // @see AgoraRTCError
            if (e.hasOwnProperty('code')) {
                if (e.code === 'PERMISSION_DENIED') {
                    this.errorType = 'permission'
                    this.queueCwLogs(`[${this.cwLogsUserType}] updateLocalDevices: AgoraRTCError PERMISSION_DENIED`)
                    return
                }
            }
            this.errorType = 'unknown'
            this.queueCwLogs(`[${this.cwLogsUserType}] updateLocalDevices: Error UNKNOWN ${e}`)
        },

        /**
         * カメラ接続状態を更新
         *
         * @param {object} videos
         */
        updateLocalVideos: function (videos) {
            const prev = this.videos.map(video => video.deviceId)
            const current = videos.map(video => video.deviceId)
            this.videos = videos

            if (prev.length !== current.length) {
                // カメラ未接続
                if (current.length < 1) {
                    this.selectedVideoId = null
                    this.localVideoState = false
                    this.queueCwLogs(`[${this.cwLogsUserType}] updateLocalVideos: all local videos disconnected.`)
                }
                // カメラ未接続状態からカメラを接続
                else if (prev.length < 1) {
                    this.selectedVideoId = this.autoSelectDevice(this.videos)
                    this.queueCwLogs(`[${this.cwLogsUserType}] updateLocalVideos: new local video connected.`)
                }
                // 複数台カメラが接続された状態でカメラを追加
                else if (current.length > prev.length) {
                    this.queueCwLogs(`[${this.cwLogsUserType}] updateLocalVideos: additional new local video connected.`)
                }
                // 複数台カメラが接続された状態でカメラを解除
                else if (prev.length > current.length) {
                    const currentVideo = this.videos.find(video => {
                        return video.deviceId === this.selectedVideoId
                    })
                    if (!currentVideo) {
                        this.selectedVideoId = this.autoSelectDevice(this.videos)
                    }
                    this.queueCwLogs(`[${this.cwLogsUserType}] updateLocalVideos: part of local video disconnected.`)
                }

                this.logLocalTracks()
            }
            this.localVideoState = !!this.selectedVideoId
        },

        /**
         * マイク接続状態を更新
         *
         * @param {object} audios
         */
        updateLocalAudios: function (audios) {
            const prev = this.audios.map(audio => audio.deviceId)
            const current = audios.map(audio => audio.deviceId)
            this.audios = audios

            if (prev.length !== current.length) {
                // マイク未接続
                if (current.length < 1) {
                    this.selectedAudioId = null
                    this.queueCwLogs(`[${this.cwLogsUserType}] updateLocalAudios: all local audios disconnected.`)
                }
                // マイク未接続状態からマイクを接続
                else if (prev.length < 1) {
                    this.selectedAudioId = this.autoSelectDevice(this.audios)
                    this.queueCwLogs(`[${this.cwLogsUserType}] updateLocalAudios: new local audio connected.`)
                }
                // 複数台マイクが接続された状態でマイクを追加
                else if (current.length > prev.length) {
                    this.queueCwLogs(`[${this.cwLogsUserType}] updateLocalAudios: additional new local audio connected.`)
                }
                // 複数台マイクが接続された状態でマイクを解除
                else if (prev.length > current.length) {
                    const currentAudio = this.audios.find(audio => {
                        return audio.deviceId === this.selectedAudioId
                    })
                    if (!currentAudio) {
                        this.selectedAudioId = this.autoSelectDevice(this.audios)
                    }
                    this.queueCwLogs(`[${this.cwLogsUserType}] updateLocalAudios: part of local audio disconnected.`)
                }

                this.logLocalTracks()
            }
            this.localAudioState = !!this.selectedAudioId
        },

        /**
         * カメラ／マイクの自動切替
         *
         * @param {array} devices
         */
        autoSelectDevice: function (devices) {
            if (devices.length < 1) return null

            // システムデフォルトのデバイスを優先選択する（見つからない場合は先頭のデバイスを選択）
            const device = devices.find((device) => {
                return device.deviceId === 'default'
                    || device.label.includes('Built-in')
                    || device.label.includes('規定')
                    || device.label.includes('内蔵')
            }) || devices[0]

            if (device) {
                this.queueCwLogs(`[${this.cwLogsUserType}] autoSelectDevice label:${device.label} kind:${device.kind} deviceId:${device.deviceId} groupId:${device.groupId}`)
            }
            return device ? device.deviceId : null
        },

        /**
         * ローカルデバイスの状態をログ収集
         */
        logLocalTracks: function () {
            if (!this.uplinkClient || !this.uplinkClient.localTracks) {
                return
            }
            this.uplinkClient.localTracks.forEach((track, index) => {
                this.queueCwLogs(`[${this.cwLogsUserType}] localTrackInfo[${index}] label:${track.getTrackLabel()} type:${track.trackMediaType} trackId:${track.getTrackId()} enabled:${track.enabled} isPlaying:${track.isPlaying}`)
            })
        },
    },
}
</script>

<style lang="scss" scoped>
.testResult {
    h6 {
        font-size: 1rem;
        font-weight: bold;

        svg {
            fill: #F79E06;
        }

        &.success {
            svg {
                fill: #2cc561;
            }
        }
    }

    ol {
        padding-left: 1.2rem;

        li {
            img {
                width: 32px;
                margin: 0 4px;
            }
        }
    }
}
</style>
